import {createEntityAdapter, createSlice, EntityState, PayloadAction} from '@reduxjs/toolkit';
import {GetBeersDTO} from 'typescript/dto/beersDto';
import {Beer} from 'typescript/models/Beer';
import Brewery from 'typescript/models/Brewery';
import {PubBeer} from 'typescript/models/PubBeer';
import {RootState} from 'typescript/types/RootState';

import {beerManagerModes} from './typescript';

const breweryAdapter = createEntityAdapter<Brewery>({
  selectId: (brewery) => brewery.id,
});

export interface IState {
  mode: beerManagerModes;
  pubBeers: PubBeer[];
  allBeers: Beer[];
  checkedBeer: {id: number; name: string; imageUrl: string} | null;
  removedBeer: {id: number; name: string} | null;
  beersBreweriesIncluded: {id: string; type: string; attributes: Brewery['attributes']}[];
  currentPage: number;
  totalPages: number;
  currentSearch: string;
  fallbackSearch: string;
  changeSearch: boolean;
  beerTotalPages: number;
  allBeerFilter: GetBeersDTO;
  pubBeerFilters: {page: number; perPage: number};
  breweryAdapter: EntityState<Brewery>;
}

const initialState: IState = {
  mode: beerManagerModes.listBeers,
  pubBeers: [],
  allBeers: [],
  checkedBeer: null,
  removedBeer: null,
  beersBreweriesIncluded: [],
  currentPage: 1,
  totalPages: 0,
  currentSearch: '',
  fallbackSearch: '',
  changeSearch: false,
  beerTotalPages: 0,
  allBeerFilter: {page: 1, perPage: 8, name: ''},
  pubBeerFilters: {page: 1, perPage: 50},
  breweryAdapter: breweryAdapter.getInitialState(),
};

export const beersManagerNamespace = 'beersManager';

const beerManagerSlice = createSlice({
  name: beersManagerNamespace,
  initialState,
  reducers: {
    setBreweryAdapterAC: (state, action: PayloadAction<Brewery[]>) => {
      breweryAdapter.setAll(state.breweryAdapter, action.payload);
    },
    changeModeAC: (state, action: PayloadAction<beerManagerModes>) => {
      state.mode = action.payload;
    },
    setPubBeersAC: (state, action: PayloadAction<PubBeer[]>) => {
      state.pubBeers = action.payload;
    },
    addPubBeerAC: (state, action: PayloadAction<PubBeer>) => {
      state.pubBeers.push(action.payload);
    },
    setAllBeersAC: (state, action: PayloadAction<Beer[]>) => {
      state.allBeers = action.payload;
    },
    setCheckedBeerAC: (state, action: PayloadAction<IState['checkedBeer']>) => {
      state.checkedBeer = action.payload;
    },
    setBeersBreweriesIncludedAC: (state, action: PayloadAction<IState['beersBreweriesIncluded']>) => {
      state.beersBreweriesIncluded = action.payload;
    },
    setCurrentPageAC: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setTotalPagesAC: (state, action: PayloadAction<number>) => {
      state.totalPages = action.payload;
    },
    setBeersTotalPagesAC: (state, action: PayloadAction<number>) => {
      state.beerTotalPages = action.payload;
    },
    setCurrentSearchAC: (state, action: PayloadAction<string>) => {
      state.currentSearch = action.payload;
    },
    setFallbackSearchAC: (state, action: PayloadAction<string>) => {
      state.fallbackSearch = action.payload;
    },
    setChangeSearchAC: (state, action: PayloadAction<boolean>) => {
      state.changeSearch = action.payload;
    },
    setRemovedBeerAC: (state, action: PayloadAction<IState['checkedBeer']>) => {
      state.removedBeer = action.payload;
    },
    setAllBeersFilterAC: (state, action: PayloadAction<IState['allBeerFilter']>) => {
      state.allBeerFilter = action.payload;
    },
    setPubBeerFiltersAC: (state, action: PayloadAction<IState['pubBeerFilters']>) => {
      state.pubBeerFilters = action.payload;
    },
  },
});

export default beerManagerSlice;

export const brewerySelector = breweryAdapter.getSelectors<RootState['beersManager']>((state) => state.breweryAdapter);

export const {
  setBreweryAdapterAC,
  setPubBeerFiltersAC,
  setAllBeersFilterAC,
  setRemovedBeerAC,
  changeModeAC,
  setPubBeersAC,
  addPubBeerAC,
  setAllBeersAC,
  setCheckedBeerAC,
  setCurrentSearchAC,
  setFallbackSearchAC,
  setChangeSearchAC,
  setCurrentPageAC,
  setTotalPagesAC,
  setBeersTotalPagesAC,
  setBeersBreweriesIncludedAC,
} = beerManagerSlice.actions;
